import { useState } from 'react'
import { useStore } from '../store/AppStore'
import { appProgress, upcomingRequirements } from '../store/selectors'
import { countdown, fmtDate, isOverdue, relDay, today } from '../lib/date'
import { Button, Card, Check, Empty, Field, Meta, Progress, ScreenHead, Sheet, hueVars } from '../components/ui'
import { IconPlus, IconTrash } from '../components/icons'
import { APP_STATUSES, type AppStatus, type Application } from '../store/types'
import { useT, type TKey } from '../i18n'

const statusOf = (id: AppStatus) => APP_STATUSES.find((s) => s.id === id)!

export default function Journey() {
  const {
    state,
    addApplication,
    updateApplication,
    removeApplication,
    toggleRequirement,
    addRequirement,
    removeRequirement,
  } = useStore()
  const [editing, setEditing] = useState<Application | 'new' | null>(null)
  const t = useT()

  const apps = [...state.applications].sort((a, b) => a.deadline.localeCompare(b.deadline))
  const next = upcomingRequirements(state, 5)

  return (
    <>
      <ScreenHead
        title={t('journey.title')}
        sub={t('journey.subtitle')}
        action={
          <Button variant="accent" onClick={() => setEditing('new')}>
            <IconPlus /> {t('common.add')}
          </Button>
        }
      />

      {next.length > 0 && (
        <Card sunk style={{ marginBottom: 'var(--s-5)' }}>
          <Meta>{t('journey.whatsNext')}</Meta>
          <div className="stack stack--sm" style={{ marginTop: 'var(--s-3)' }}>
            {next.map(({ app, req, due }) => (
              <div key={req.id} className="row">
                <span
                  className="chip__dot"
                  style={{ background: `var(--h-${statusOf(app.status).hue})` }}
                />
                <span className="grow truncate" style={{ fontSize: 'var(--t-sm)' }}>
                  {req.label}
                </span>
                <span className={`countdown ${isOverdue(due) ? 'countdown--urgent' : ''}`}>
                  {relDay(due)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="stack">
        {apps.length === 0 ? (
          <Empty title={t('journey.noApplications')} body={t('journey.noApplicationsBody')} />
        ) : (
          apps.map((app) => {
            const status = statusOf(app.status)
            const pct = appProgress(app)
            const urgent = isOverdue(app.deadline)
            return (
              <Card key={app.id} style={hueVars(status.hue)}>
                <div className="row" style={{ alignItems: 'flex-start' }}>
                  <div className="grow">
                    <Meta>{app.institution}</Meta>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--t-md)',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        marginTop: 4,
                      }}
                    >
                      {app.course}
                    </h3>
                  </div>
                  <span className="appcard__status">{t(status.labelKey as TKey)}</span>
                </div>

                <div className="row" style={{ marginTop: 'var(--s-3)' }}>
                  <span className="countdown grow">
                    {t('journey.deadline', {
                      date: fmtDate(app.deadline, { weekday: undefined, year: 'numeric' }),
                    })}
                  </span>
                  <span className={`countdown ${urgent ? 'countdown--urgent' : ''}`}>
                    {countdown(app.deadline)}
                  </span>
                </div>

                <div style={{ margin: 'var(--s-3) 0 var(--s-4)' }}>
                  <Progress value={pct} hue={status.hue} />
                </div>

                <div className="stack stack--sm">
                  {app.requirements.map((r) => (
                    <Check
                      key={r.id}
                      checked={r.done}
                      hue={status.hue}
                      onChange={() => toggleRequirement(app.id, r.id)}
                      label={
                        <>
                          <span style={{ fontSize: 'var(--t-sm)' }}>{r.label}</span>
                          {r.due && (
                            <span
                              className="countdown"
                              style={{
                                display: 'block',
                                marginTop: 2,
                                color: !r.done && isOverdue(r.due) ? 'var(--danger)' : 'var(--ink-3)',
                              }}
                            >
                              {relDay(r.due)}
                            </span>
                          )}
                        </>
                      }
                      right={
                        <Button
                          icon
                          variant="quiet"
                          aria-label={t('journey.removeRequirement', { name: r.label })}
                          onClick={() => removeRequirement(app.id, r.id)}
                        >
                          <IconTrash />
                        </Button>
                      }
                    />
                  ))}
                  <AddRequirement onAdd={(label) => addRequirement(app.id, label)} />
                </div>

                {app.notes && (
                  <p
                    style={{
                      marginTop: 'var(--s-4)',
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--t-sm)',
                      color: 'var(--ink-2)',
                      lineHeight: 'var(--lh-snug)',
                    }}
                  >
                    {app.notes}
                  </p>
                )}

                <div className="row row--wrap" style={{ marginTop: 'var(--s-4)', gap: 'var(--s-2)' }}>
                  {APP_STATUSES.map((s) => (
                    <button
                      key={s.id}
                      className="chip"
                      aria-pressed={app.status === s.id}
                      onClick={() => updateApplication(app.id, { status: s.id })}
                    >
                      {t(s.labelKey as TKey)}
                    </button>
                  ))}
                  <span className="grow" />
                  <Button variant="quiet" size="sm" onClick={() => setEditing(app)}>
                    {t('common.edit')}
                  </Button>
                </div>
              </Card>
            )
          })
        )}
      </div>

      {editing && (
        <ApplicationSheet
          initial={editing === 'new' ? {} : editing}
          onClose={() => setEditing(null)}
          onSave={(data) => {
            if (editing === 'new') addApplication(data)
            else updateApplication(editing.id, data)
            setEditing(null)
          }}
          onDelete={
            editing === 'new'
              ? undefined
              : () => {
                  removeApplication(editing.id)
                  setEditing(null)
                }
          }
        />
      )}
    </>
  )
}

function AddRequirement({ onAdd }: { onAdd: (label: string) => void }) {
  const [value, setValue] = useState('')
  const t = useT()
  const submit = () => {
    if (!value.trim()) return
    onAdd(value.trim())
    setValue('')
  }
  return (
    <div className="row" style={{ marginTop: 'var(--s-2)' }}>
      <input
        className="input grow"
        dir="auto"
        placeholder={t('journey.addRequirement')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        aria-label={t('journey.newRequirement')}
      />
      <Button icon variant="ghost" onClick={submit} disabled={!value.trim()} aria-label={t('common.add')}>
        <IconPlus />
      </Button>
    </div>
  )
}

function ApplicationSheet({
  initial,
  onClose,
  onSave,
  onDelete,
}: {
  initial: Partial<Application>
  onClose: () => void
  onSave: (a: Omit<Application, 'id'>) => void
  onDelete?: () => void
}) {
  const [institution, setInstitution] = useState(initial.institution ?? '')
  const [course, setCourse] = useState(initial.course ?? '')
  const [deadline, setDeadline] = useState(initial.deadline ?? today())
  const [status, setStatus] = useState<AppStatus>(initial.status ?? 'researching')
  const [notes, setNotes] = useState(initial.notes ?? '')
  const t = useT()

  const submit = () => {
    if (!institution.trim()) return
    onSave({
      institution: institution.trim(),
      course: course.trim(),
      deadline,
      status,
      notes: notes.trim() || undefined,
      requirements: initial.requirements ?? [],
    })
  }

  return (
    <Sheet
      title={t(initial.id ? 'journey.editApplication' : 'journey.newApplication')}
      onClose={onClose}
      footer={
        <>
          <Button variant="accent" block onClick={submit} disabled={!institution.trim()}>
            {t('common.save')}
          </Button>
          {onDelete && (
            <Button variant="danger" icon onClick={onDelete} aria-label={t('journey.deleteApplication')}>
              <IconTrash />
            </Button>
          )}
        </>
      }
    >
      <Field label={t('journey.fieldInstitution')}>
        <input
          className="input"
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          dir="auto"
          placeholder={t('journey.institutionPlaceholder')}
        />
      </Field>
      <Field label={t('journey.fieldCourse')}>
        <input
          className="input"
          value={course}
          onChange={(e) => setCourse(e.target.value)}
          dir="auto"
          placeholder={t('journey.coursePlaceholder')}
        />
      </Field>
      <Field label={t('journey.fieldDeadline')}>
        <input
          className="input"
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
        />
      </Field>
      <div className="field">
        <span>{t('journey.fieldStatus')}</span>
        <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
          {APP_STATUSES.map((s) => (
            <button
              key={s.id}
              className="chip"
              aria-pressed={status === s.id}
              onClick={() => setStatus(s.id)}
            >
              <span className="chip__dot" style={{ background: `var(--h-${s.hue})` }} />
              {t(s.labelKey as TKey)}
            </button>
          ))}
        </div>
      </div>
      <Field label={t('journey.fieldNotes')}>
        <textarea
          className="textarea"
          dir="auto"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('journey.notesPlaceholder')}
        />
      </Field>
    </Sheet>
  )
}
